/** API presence is not availability (managed browsers often return a null adapter). */
export async function hasWebGpu(nav: { gpu?: { requestAdapter(): Promise<unknown> } }): Promise<boolean> {
  try {
    return Boolean(await nav.gpu?.requestAdapter());
  } catch {
    return false;
  }
}

export type LocalBackend = "webgpu" | "wasm";
export interface LocalClassifier<T> {
  (text: string): Promise<T>;
  dispose?(): Promise<void>;
}

/** Keep successful sessions, but never retain a rejected initialization promise. */
export function createLocalInference<T>(options: {
  supportsGpu: () => Promise<boolean>;
  load: (backend: LocalBackend) => Promise<LocalClassifier<T>>;
  onBackend: (backend: LocalBackend) => void;
}) {
  let session: Promise<LocalClassifier<T>> | null = null;
  let backend: LocalBackend = "wasm";
  let gpuFailed = false;

  async function load(asked: LocalBackend): Promise<LocalClassifier<T>> {
    backend = asked;
    options.onBackend(backend);
    try {
      return await options.load(backend);
    } catch (error) {
      if (backend !== "webgpu") throw error;
      gpuFailed = true;
      return load("wasm");
    }
  }

  async function getSession() {
    if (!session) {
      session = (async () => load(!gpuFailed && await options.supportsGpu() ? "webgpu" : "wasm"))()
        .catch(error => { session = null; throw error; });
    }
    return session;
  }

  return async (text: string): Promise<T> => {
    const classifier = await getSession();
    try {
      return await classifier(text);
    } catch (error) {
      session = null;
      try { await classifier.dispose?.(); } catch { /* Device may already be lost. */ }
      if (backend !== "webgpu") throw error;
      gpuFailed = true;
      session = load("wasm").catch(error => { session = null; throw error; });
      const fallback = await session;
      try { return await fallback(text); }
      catch (error) {
        session = null;
        try { await fallback.dispose?.(); } catch { /* Preserve inference error. */ }
        throw error;
      }
    }
  };
}
